import { invalidate, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';
import { Matrix4, Vector2, Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { useArticleDragHandler } from 'app/modules/wallforming/components/input-binding/hooks/useArticleDragHandler';
import { useInputEventHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputEventHandler';
import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { resolveHoverArticleRef } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { raycastPlane } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { getDimensionHiddenSheetArticles } from 'app/modules/wallforming/components/scenes/objects/articles/utils/sheetVisibilityUtils';
import {
    getCanvasPointer,
    inputPointerPosition
} from 'app/modules/wallforming/components/scenes/objects/utils/pointerUtils';
import { isSceneToolActive } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const useMainSceneEventHandler = () => {
    const {
        handleOnWindowKeyUp,
        handleOnWindowPointerUp,
        handleOnWindowPointerDown,
        handleOnDragArticle,
        setPointerPositionRef,
        mainRaycasterRef,
        mainCameraProjectionType,
        manualPlacementData,
        waitForPlacementData
    } = usePlacementStore(
        useShallow((state) => ({
            handleOnWindowKeyUp: state.handleOnWindowKeyUp,
            handleOnWindowPointerUp: state.handleOnWindowPointerUp,
            handleOnWindowPointerDown: state.handleOnWindowPointerDown,
            handleOnDragArticle: state.handleOnDragArticle,
            setPointerPositionRef: state.setPointerPositionRef,
            mainRaycasterRef: state.mainRaycasterRef,
            mainCameraProjectionType: state.mainCameraProjectionType,
            manualPlacementData: state.manualPlacementData,
            waitForPlacementData: state.waitForPlacementData
        }))
    );

    const initializeSelectionBox = usePlacementStore(useShallow((state) => state.initializeSelectionBox));

    useInputEventHandler();
    useArticleDragHandler();

    const scene = useThree((state) => state.scene);
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);
    const size = useThree((state) => state.size);
    const updatePointerEvents = useThree((state) => state.events.update);
    const isToolActive = usePlacementStore(isSceneToolActive);
    const isQuickReplacementGridActive = usePlacementStore(
        (state) => state.isPanelQuickReplacementOpen && state.isPanelQuickReplacementGridActive
    );
    const dimensionHiddenSheetArticles = usePlacementStore(getDimensionHiddenSheetArticles);
    const previousDimensionHiddenSheetArticles = useRef<Record<string, string[]> | undefined>(undefined);

    useEffect(() => {
        const state = usePlacementStore.getState();
        const affected = new Map<string, Set<string>>();
        for (const articles of [previousDimensionHiddenSheetArticles.current, dimensionHiddenSheetArticles]) {
            for (const [articleId, instanceIds] of Object.entries(articles ?? {})) {
                const ids = affected.get(articleId) ?? new Set<string>();
                instanceIds.forEach((id) => ids.add(id));
                affected.set(articleId, ids);
            }
        }
        previousDimensionHiddenSheetArticles.current = dimensionHiddenSheetArticles;

        for (const [articleId, ids] of affected) {
            const article = state.articles[articleId];
            const instanceIds = [...ids].filter((id) => !!article?.articleInstances[id]);
            if (!article || instanceIds.length === 0) {
                continue;
            }
            state.updateInstanceMatrices(
                true,
                {
                    current: {
                        id: articleId,
                        articleNumber: article.articleNumber,
                        isMainCanvas: true,
                        lifeCycleCode: article.lifeCycleCode,
                        articleRepresentations: [],
                        articleInstances: article.articleInstances,
                        edgeGeometries: article.edgeGeometries
                    }
                },
                false,
                instanceIds
            );
        }
    }, [dimensionHiddenSheetArticles]);

    useEffect(() => {
        const state = usePlacementStore.getState();
        for (const instanceId of Object.keys(state.articleInstanceSelections)) {
            const articleId = state.instanceBatchIds.get(instanceId)?.articleId;
            if (articleId) {
                const articleRef = resolveHoverArticleRef(state, articleId, instanceId, { current: undefined });
                if (articleRef) {
                    state.updateInstanceMatrices(true, articleRef, false, [instanceId]);
                }
            }
        }
    }, [isQuickReplacementGridActive]);

    useEffect(() => {
        if (isToolActive) {
            usePlacementStore.getState().clearHoveredArticleHighlight();
        }
        updatePointerEvents?.();
    }, [isToolActive, updatePointerEvents]);

    const ndcPointer = useRef<Vector2>(new Vector2());
    const pointerPlanePoint = useRef<Vector3>(new Vector3());
    const pointerEventRef = useRef<PointerEvent | null>(null);
    const pointerMovePendingRef = useRef(false);
    const cameraMatrixRef = useRef(new Matrix4());
    const projectionMatrixRef = useRef(new Matrix4());
    const previousSizeRef = useRef(size);
    const previousSelectionRef = useRef(usePlacementStore.getState().articleInstanceSelections);
    const previousPlacementDataRef = useRef(usePlacementStore.getState().manualPlacementData);
    const previousWaitForPlacementDataRef = useRef(false);
    const previousDragRefCountRef = useRef(0);
    const prevProjectionTypeRef = useRef<CameraProjectionType>(mainCameraProjectionType);

    const processPointer = useCallback(
        (event: Pick<PointerEvent, 'clientX' | 'clientY' | 'buttons' | 'altKey'>) => {
            const raycaster = mainRaycasterRef.current;
            if (
                !raycaster ||
                !getCanvasPointer(gl.domElement, ndcPointer.current, { x: event.clientX, y: event.clientY })
            ) {
                return;
            }

            camera.updateMatrixWorld();
            raycaster.setFromCamera(ndcPointer.current, camera);
            if (raycaster.ray.intersectPlane(raycastPlane, pointerPlanePoint.current)) {
                setPointerPositionRef(pointerPlanePoint.current);
            } else {
                setPointerPositionRef(null);
            }
            handleOnDragArticle({
                buttons: event.buttons,
                clientX: event.clientX,
                clientY: event.clientY,
                ray: raycaster.ray,
                altKey: event.altKey
            });
        },
        [camera, gl.domElement, handleOnDragArticle, mainRaycasterRef, setPointerPositionRef]
    );

    const flushPointer = useCallback(
        (event: PointerEvent) => {
            if (event.target !== gl.domElement) {
                return;
            }
            const state = usePlacementStore.getState();
            if (pointerMovePendingRef.current || state.isAutoDragActiveRef.current || state.isDraggingActive()) {
                pointerMovePendingRef.current = false;
                processPointer({
                    clientX: event.clientX,
                    clientY: event.clientY,
                    altKey: event.altKey,
                    buttons: pointerEventRef.current?.buttons ?? event.buttons
                });
            }
        },
        [gl.domElement, processPointer]
    );

    const handleWindowPointerUp = useCallback(
        (event: PointerEvent) => {
            flushPointer(event);
            pointerMovePendingRef.current = false;
            pointerEventRef.current = null;
            handleOnWindowPointerUp(event);
        },
        [flushPointer, handleOnWindowPointerUp]
    );

    const handleWindowPointerDown = useCallback(
        (event: PointerEvent) => {
            flushPointer(event);
            handleOnWindowPointerDown(event);
        },
        [flushPointer, handleOnWindowPointerDown]
    );

    useFrame(() => {
        const state = usePlacementStore.getState();
        const active =
            state.isAutoDragActiveRef.current || state.isDragNewArticleStartRef.current || state.isDraggingActive();
        const changed =
            !cameraMatrixRef.current.equals(camera.matrixWorld) ||
            !projectionMatrixRef.current.equals(camera.projectionMatrix) ||
            previousSizeRef.current !== size ||
            previousSelectionRef.current !== state.articleInstanceSelections ||
            previousPlacementDataRef.current !== state.manualPlacementData ||
            previousWaitForPlacementDataRef.current !== state.waitForPlacementData ||
            previousDragRefCountRef.current !== state.articleDragRefsMap.current.size;

        cameraMatrixRef.current.copy(camera.matrixWorld);
        projectionMatrixRef.current.copy(camera.projectionMatrix);
        previousSizeRef.current = size;
        previousSelectionRef.current = state.articleInstanceSelections;
        previousPlacementDataRef.current = state.manualPlacementData;
        previousWaitForPlacementDataRef.current = state.waitForPlacementData;
        previousDragRefCountRef.current = state.articleDragRefsMap.current.size;

        if (!pointerMovePendingRef.current && !(active && changed)) {
            return;
        }
        pointerMovePendingRef.current = false;
        const event = pointerEventRef.current;
        if (state.isAutoDragActiveRef.current || event) {
            processPointer({
                clientX: inputPointerPosition.x,
                clientY: inputPointerPosition.y,
                buttons: state.isAutoDragActiveRef.current ? 0 : (event?.buttons ?? 0),
                altKey: inputPointerPosition.altKey
            });
        }
    });

    useEffect(() => {
        invalidate();
    }, [manualPlacementData, waitForPlacementData]);

    useEffect(() => {
        const updateModifiers = (event: KeyboardEvent) => {
            if (inputPointerPosition.altKey === event.altKey) {
                return;
            }
            inputPointerPosition.altKey = event.altKey;
            const state = usePlacementStore.getState();
            if (state.isAutoDragActiveRef.current || state.isDraggingActive()) {
                pointerMovePendingRef.current = true;
                invalidate();
            }
        };
        window.addEventListener('keydown', updateModifiers);
        window.addEventListener('keyup', updateModifiers);
        return () => {
            window.removeEventListener('keydown', updateModifiers);
            window.removeEventListener('keyup', updateModifiers);
        };
    }, []);

    useEffect(() => {
        if (prevProjectionTypeRef.current !== mainCameraProjectionType) {
            prevProjectionTypeRef.current = mainCameraProjectionType;
            initializeSelectionBox(scene);
        }
    }, [initializeSelectionBox, scene, mainCameraProjectionType]);

    useEffect(() => {
        initializeSelectionBox(scene);
        window.addEventListener('pointerup', handleWindowPointerUp);
        window.addEventListener('pointerdown', handleWindowPointerDown);

        return () => {
            window.removeEventListener('pointerup', handleWindowPointerUp);
            window.removeEventListener('pointerdown', handleWindowPointerDown);
        };
    }, [handleWindowPointerUp, handleWindowPointerDown, initializeSelectionBox, scene]);

    const handleCanvasPointerMove = useCallback(
        (event: PointerEvent) => {
            if (event.target !== gl.domElement) {
                return;
            }
            pointerEventRef.current = event;
            pointerMovePendingRef.current = true;
            invalidate();
        },
        [gl.domElement]
    );

    const handleCanvasPointerLeave = useCallback(() => {
        pointerMovePendingRef.current = false;
        pointerEventRef.current = null;
        setPointerPositionRef(null);
    }, [setPointerPositionRef]);

    useEffect(() => {
        const canvas = gl.domElement;
        canvas.addEventListener('pointermove', handleCanvasPointerMove);
        canvas.addEventListener('pointerleave', handleCanvasPointerLeave);

        return () => {
            canvas.removeEventListener('pointermove', handleCanvasPointerMove);
            canvas.removeEventListener('pointerleave', handleCanvasPointerLeave);
        };
    }, [gl.domElement, handleCanvasPointerLeave, handleCanvasPointerMove]);

    useEffect(() => {
        window.addEventListener('keyup', handleOnWindowKeyUp);
        return () => {
            window.removeEventListener('keyup', handleOnWindowKeyUp);
        };
    }, [handleOnWindowKeyUp]);
};
